import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RepeatIcon from "@mui/icons-material/Repeat";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";

import { StyledCard } from "../common/StyledCard";
import { VoteHeadResponseDto } from "../../shared/dtos/voteHead/VoteHeadResponseDto";

interface VoteHeadCardProps {
  voteHead: VoteHeadResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const VoteHeadCard: React.FC<VoteHeadCardProps> = ({
  voteHead,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceWalletOutlinedIcon color="primary" />
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                flexWrap: "noWrap",
              }}
            >
              {voteHead.name}
            </Typography>
          </Box>
          <Stack direction="row">
            {onEdit && (
              <Tooltip title="Edit Vote Head">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Vote Head">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Body */}
        <Box sx={{ flexGrow: 1, mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {voteHead.description || "No description provided."}
          </Typography>
        </Box>

        {/* Footer */}
        <Stack
          direction="row"
          sx={{
            spacing: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Chip
            label={`Priority: ${voteHead.priority}`}
            size="small"
            variant="outlined"
            icon={<FormatListNumberedIcon fontSize="small" />}
          />
          {voteHead.recurring && (
            <Chip
              label="Recurring"
              size="small"
              color="success"
              variant="outlined"
              icon={<RepeatIcon fontSize="small" />}
            />
          )}

          <Chip
            label={voteHead.isOptional ? "Optional" : "Compulsory"}
            size="small"
            color={voteHead.isOptional ? "info" : "warning"}
            variant="outlined"
            icon={<HelpOutlineIcon fontSize="small" />}
          />

          {voteHead.amount !== null &&
            voteHead.amount !== undefined &&
            voteHead.amount > 0 && (
              <Chip
                label={`KES ${voteHead.amount.toLocaleString()}`}
                size="small"
                color="secondary"
                variant="outlined"
                icon={<PaidOutlinedIcon fontSize="small" />}
              />
            )}
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default VoteHeadCard;
